import { useEffect, useState } from 'react'
import { Button } from '@progress/kendo-react-buttons'
import { SearchBody } from '../../../components/layouts'
import { SupplierCondition } from './SupplierCondition'
import { SupplierResultList } from './SupplierResultList'
import { Window } from '@progress/kendo-react-dialogs'
import { useAccessibleAuthority, usePageTitle } from '../../../components/hooks'
import { useNavigate } from 'react-router-dom'
import {
  SupplierSearchActions,
  CommonActions,
  useAppDispatch,
  useAppSelector
} from '../../../stores'
import { ErrorMessages, ErrorMessage, ConfirmDialog } from '../../../components/parts'
import { MessageProvider } from '../../../services'
import { Authority } from '../../../gateway/system/Authority'
import Constants from '../../../data/Constants.json'
import { SearchCondition } from '@gateway.maintenance'
import { SupplierListGateway } from '../../../gateway/maintenance/SupplierListGateway'
import { SupplierInput } from './SupplierInput'

/**
 * SupplierSearch 検索条件初期値
 */
const SUPPLIER_CONDATION_INITIAL_STATE: SearchCondition = {
  supplierNm: '',
  supplierBranchNm: null,
  branches: [],
  supplierPerson: '',
  expense: [],
  buildingDiscloseFlg: [],
  ourCompanyCapacityFlg: [],
  soyukaiFlg: [],
  hearingSheetFlg: [],
  replyReqFlg: []
}

export const SupplierSearch = () => {
  useAccessibleAuthority(Authority.Builder, Authority.Material, Authority.Branch)
  usePageTitle('業者マスタメンテナンス')

  const navigate = useNavigate()
  const [showMaintenanceDialog, setShowMaintenanceDialog] = useState(false)
  const [confirmRegistDialogGoBackShow, setConfirmRegistDialogGoBackShow] = useState(false)
  const [notificationMessages, setNotificationMessages] = useState<ErrorMessage[]>([])
  const [errorMessages, setErrorMessages] = useState<ErrorMessage[]>([])
  // 取得された値
  const [searchCondition, setSearchCondition] = useState<SearchCondition>(
    SUPPLIER_CONDATION_INITIAL_STATE
  )
  const searchResult = useAppSelector(
    (state) => state.supplierSearch.searchResult
  )
  // スキップ明細数
  const [skip, setSkip] = useState(0)
  // 選択業者
  const [target, setTarget] = useState('')

  const dispatch = useAppDispatch()

  const userProp = useAppSelector((x) => x.common.userProp)

  const toggleMaintenanceDialog = () => {
    setShowMaintenanceDialog(!showMaintenanceDialog)
  }

  // 工務権限以外の判定フラグ
  const isNotBuilderAuthority = userProp?.authority !== Authority.Builder

  const initSearchCondition = () => {
    let branches: string[] = []
    // ログインユーザが建材部担当者の場合
    if (userProp?.authority === Authority.Material) {
      branches = Constants.kantoFourBranches
    }

    // ログインユーザが支店担当者の場合
    if (userProp?.authority === Authority.Branch) {
      branches = [userProp?.branchCode]
    }

    return {
      ...SUPPLIER_CONDATION_INITIAL_STATE,
      branches
    }
  }

  /**
   * 新規追加ダイアログのクローズ処理
   */
  const confirmRegistDialogGoBackBtn = () => {
    setShowMaintenanceDialog(false)
    setConfirmRegistDialogGoBackShow(false)
  }

  /**
   * 検索ボタン押下
   */
  const handleSearch = (searchCondition: SearchCondition) => {
    dispatch(CommonActions.setIsLoading(true))
    setSkip(0)
    setTarget('')
    SupplierListGateway.fetchSupplierSearchList(searchCondition)
      .then((res) => {
        if (res.error) {
          if (Array.isArray(res.messages) && res.messages.length > 0) {
            const errorMessages = res.messages.map<ErrorMessage>((message) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(errorMessages)
          }
        } else {
          // 検索結果存在チェック
          if (res.data.infoList.length === 0) {
            const message: ErrorMessage[] = [{
              id: 'I001',
              content: MessageProvider.provide(
                'I001', ...[]
              )
            }]
            setNotificationMessages(message)
            dispatch(SupplierSearchActions.setSearchResult(res.data.infoList))
          } else if (res.data.infoList.length > 1000) {
            // 1000件以上の場合
            const message: ErrorMessage[] = [{
              id: 'I015',
              content: MessageProvider.provide(
                'I015', ...['1000']
              )
            }]
            setNotificationMessages(message)
            dispatch(SupplierSearchActions.setSearchResult(res.data.infoList.slice(0, 1000)))
            // 検索データストアを作成する
            dispatch(SupplierSearchActions.setSearchCondition(searchCondition))
          } else {
            // 1~1000件以内の場合
            dispatch(SupplierSearchActions.setSearchResult(res.data.infoList))
            // 検索データストアを作成する
            dispatch(SupplierSearchActions.setSearchCondition(searchCondition))
          }
        }
      })
      .finally(() => { dispatch(CommonActions.setIsLoading(false)) })
  }
  /**
   * クリアボタン押下
   */
  const handleEmpty = () => {
    // 初期検索条件
    const initailSerachCondition = initSearchCondition()

    // 初期検索条件の設定
    setSearchCondition(initailSerachCondition)
  }
  /**
   * 戻るボタン押下
   */
  const handleReturn = () => {
    // データストアを破棄する
    dispatch(
      SupplierSearchActions.setSearchCondition(SUPPLIER_CONDATION_INITIAL_STATE)
    )
    dispatch(SupplierSearchActions.setSearchResult([]))

    navigate(-1)
  }

  /**
   * 初期表示
   */
  useEffect(() => {
    // 初期検索条件
    const initailSerachCondition = initSearchCondition()

    // 初期検索条件の設定
    setSearchCondition(initailSerachCondition)

    // 初期検索
    handleSearch(initailSerachCondition)
  }, [])

  return (
    <>
      <SearchBody
        total={searchResult.length}
        changeSkip={setSkip}
        skip={skip}
        leftPane={
          <SupplierCondition
            setSearchCondition={setSearchCondition}
            searchCondition={searchCondition}
          />
        }
        leftPaneButton={
          <>
            <Button onClick={handleEmpty}>クリア</Button>
            <Button
              themeColor="primary"
              onClick={() => handleSearch(searchCondition)}
            >
              検索
            </Button>
          </>
        }
        rightPane={<SupplierResultList
          skip={skip}
          setNotificationMessages={setNotificationMessages}
          setErrorMessages={setErrorMessages}
          setSkip={setSkip}
          target={target}
          setTarget={setTarget}
        />}
        rightPaneButton={
          <>
            <Button onClick={handleReturn}>戻る</Button>
            <Button onClick={toggleMaintenanceDialog}
              themeColor="primary"
              disabled={isNotBuilderAuthority}
            >
              新規追加
            </Button>
          </>
        }
      />
      <ErrorMessages type="notification" messages={notificationMessages} />
      <ErrorMessages type="dialog" messages={errorMessages} />
      {showMaintenanceDialog && (
        <Window
          title={'業者マスタメンテナンス　新規'}
          onClose={() => setConfirmRegistDialogGoBackShow(true)}
          initialWidth={1280}
          initialHeight={500}
          modal
        >
          <SupplierInput
            setShowMaintenanceDialog={setShowMaintenanceDialog}
            setNotificationMessages={setNotificationMessages}
            setErrorMessages={setErrorMessages}
            skip={skip}
            setSkip={setSkip}
            isFixMode={false}
            isAddSupplierBranchMode={false}
          />
          <div
            style={{
              marginTop: '10px',
              display: 'flex',
              justifyContent: 'flex-end'
            }}
          ></div>
        </Window>
      )}
      {confirmRegistDialogGoBackShow && (
        <ConfirmDialog
          title="戻る"
          open={confirmRegistDialogGoBackShow}
          renderCloseButton
          id={'I003'}
          args={['戻る']}
          onClickOk={confirmRegistDialogGoBackBtn}
          onClickCancel={() => setConfirmRegistDialogGoBackShow(false)}
        />
      )}
    </>
  )
}
